import { differenceInMonths } from "date-fns";

import { getNeutralFeeColors, Theme } from "./feeComparisonColors";

import {
  CurrencyCode,
  PlayerDetailPlayerBioDataFragment,
  PlayerDetailScoutReportsFragment,
} from "@/generated/graphql";
import { ICompositeMetric } from "@/stores/compositeMetric";
import {
  formatCustomMetricValue,
  processDynamicCustomMetrics,
} from "@/types/customMetrics";
import getColorClass, { ColorClass } from "@/utils/colorScale";
import formatCurrencyShort from "@/utils/formatCurrencyShort";

/**
 *
 */
interface FormatValue {
  value: number | null | undefined;
  isShowDefaultInCompare?: boolean;
  isCategoricalMetric?: boolean;
  currencyCode?: CurrencyCode;
  isGPR: boolean;
  isCurrency: boolean;
}

const formatValue = ({
  value,
  isShowDefaultInCompare,
  isCategoricalMetric,
  currencyCode,
  isCurrency,
}: FormatValue): string | null => {
  if (
    (isShowDefaultInCompare && isCategoricalMetric) ||
    value === null ||
    value === undefined ||
    value === -1
  ) {
    return "N/A";
  }
  if (isCategoricalMetric && value === -1) {
    return null;
  }
  if (isCurrency) {
    return formatCurrencyShort(value, currencyCode as CurrencyCode);
  }
  return Math.round(value).toString();
};

/**
 * Convert boolean isDarkMode to Theme type
 * @param isDarkMode - Whether dark mode is active
 * @returns Theme value "dark" or "light"
 */
const toTheme = (isDarkMode: boolean): Theme => (isDarkMode ? "dark" : "light");

const formatFeeRange = (
  valueLow: number | null | undefined,
  valueHigh: number | null | undefined,
  currencyCode: CurrencyCode
): string | null => {
  if (!valueLow || !valueHigh) {
    return null;
  }

  const lowerFee = Math.min(valueLow, valueHigh);
  const higherFee = Math.max(valueLow, valueHigh);

  const lowerFormatted = formatCurrencyShort(lowerFee, currencyCode);
  const higherFormatted = formatCurrencyShort(higherFee, currencyCode);

  return `${lowerFormatted} - ${higherFormatted}`;
};

const formatMetric = (
  label: string,
  value: number | null | undefined,
  min: number,
  max: number,
  currencyCode?: CurrencyCode,
  isCurrency = false,
  isGPR = false,
  isShowDefaultInCompare = false,
  isCategoricalMetric = false,
  isDefaultStyle = false,
  isDarkMode = true
) => {
  const valueTemp = value !== -1 ? value : undefined;
  return {
    label,
    formattedValue: formatValue({
      value,
      isShowDefaultInCompare,
      isCategoricalMetric,
      currencyCode,
      isCurrency,
      isGPR,
    }),
    styles: getColorClass(
      isDefaultStyle ? undefined : valueTemp,
      min,
      max,
      true,
      true,
      isDarkMode
    ),
  };
};

const formatMetricWithCustomStyles = (
  label: string,
  formattedValue: string | null,
  styles: ColorClass
): ICompositeMetric => ({
  label,
  formattedValue,
  styles,
});

const getPlayerBaseMetrics = (
  bioData: PlayerDetailPlayerBioDataFragment | null | undefined,
  currencyCode: CurrencyCode,
  isDefaultStyle: boolean,
  isDarkMode: boolean
) => [
  formatMetricWithCustomStyles(
    "Gemini Player Valuation",
    formatFeeRange(
      bioData?.minGeminiPlayerValuation ?? bioData?.fairFee,
      bioData?.maxGeminiPlayerValuation ?? bioData?.expectedFee,
      currencyCode
    ),
    getNeutralFeeColors(toTheme(isDarkMode))
  ),
  formatMetric(
    "Months Remaining",
    bioData?.contractExpires
      ? differenceInMonths(bioData.contractExpires, new Date())
      : null,
    18,
    6,
    undefined,
    false,
    false,
    false,
    false,
    false,
    isDarkMode
  ),
  formatMetric(
    "Player Style Fit",
    bioData?.fitScore ? bioData.fitScore * 100 : null,
    40,
    100,
    undefined,
    false,
    false,
    false,
    false,
    false,
    isDarkMode
  ),
  formatMetric(
    "Team Style Fit",
    bioData?.teamStyleFit,
    40,
    80,
    undefined,
    false,
    false,
    false,
    false,
    false,
    isDarkMode
  ),
  formatMetric(
    "Gemini Player Rating",
    bioData?.gpr,
    30,
    100,
    undefined,
    false,
    true,
    false,
    false,
    false,
    isDarkMode
  ),
];

const getPlayerCategoricalMetrics = (
  bioData: PlayerDetailPlayerBioDataFragment | null | undefined,
  isShowDefaultInCompare: boolean,
  isDarkMode: boolean
) => [
  formatMetric(
    "GK Passing",
    bioData?.gkPassingTransCategoricalScore,
    40,
    85,
    undefined,
    false,
    false,
    isShowDefaultInCompare,
    true,
    false,
    isDarkMode
  ),
  formatMetric(
    "Positioning",
    bioData?.gkPositioningTransCategoricalScore,
    40,
    85,
    undefined,
    false,
    false,
    isShowDefaultInCompare,
    true,
    false,
    isDarkMode
  ),
  formatMetric(
    "Shot Stopping",
    bioData?.gkShotStoppingTransCategoricalScore,
    40,
    85,
    undefined,
    false,
    false,
    isShowDefaultInCompare,
    true,
    false,
    isDarkMode
  ),
  formatMetric(
    "Carrying",
    bioData?.carryingTransGlobalCategoricalScore,
    40,
    85,
    undefined,
    false,
    false,
    isShowDefaultInCompare,
    true,
    false,
    isDarkMode
  ),
  formatMetric(
    "Dribbling",
    bioData?.dribblingTransGlobalCategoricalScore,
    40,
    85,
    undefined,
    false,
    false,
    isShowDefaultInCompare,
    true,
    false,
    isDarkMode
  ),
  formatMetric(
    "Passing",
    bioData?.passingTransGlobalCategoricalScore,
    40,
    85,
    undefined,
    false,
    false,
    isShowDefaultInCompare,
    true,
    false,
    isDarkMode
  ),
  formatMetric(
    "Shooting",
    bioData?.shotLocationTransGlobalCategoricalScore,
    40,
    85,
    undefined,
    false,
    false,
    isShowDefaultInCompare,
    true,
    false,
    isDarkMode
  ),
  formatMetric(
    "Physical Score",
    bioData?.physicalScore,
    40,
    85,
    undefined,
    false,
    false,
    isShowDefaultInCompare,
    true,
    false,
    isDarkMode
  ),
];

const getPlayerReportsMetric = (
  scoutReports: (PlayerDetailScoutReportsFragment | null)[],
  isDarkMode: boolean
) => {
  const reportsCount = scoutReports.length;
  return formatMetric(
    "Reports",
    reportsCount,
    0,
    10, // reasonable max for color scaling
    undefined,
    false,
    false,
    false,
    false,
    true, // isDefaultStyle = true for neutral coloring like Transfer Value
    isDarkMode
  );
};

// Convert custom metrics to composite metric format for display
const getPlayerCustomCompositeMetrics = (
  bioData: PlayerDetailPlayerBioDataFragment | null | undefined,
  isDarkMode: boolean
): ICompositeMetric[] => {
  if (!bioData?.customStats) {
    return [];
  }

  const customMetrics = processDynamicCustomMetrics(bioData.customStats);

  return customMetrics.map(metric => {
    const formattedValue = formatCustomMetricValue(
      metric.value,
      metric.isDecimal,
      metric.unit
    );

    // Use color scale similar to GPR (30-100 range) to avoid black middle zone
    const colorClass = getColorClass(
      metric.value,
      30, // minValue - same as GPR
      100, // maxValue - same as GPR
      true, // isHigherBetter - same as other metrics
      true, // useBorder - same as other metrics
      isDarkMode
    );

    return {
      label: metric.displayName,
      formattedValue,
      styles: colorClass,
    };
  });
};

export const getPlayerCompositeMetrics = (
  bioData: PlayerDetailPlayerBioDataFragment | null | undefined,
  currencyCode: CurrencyCode,
  isDefaultStyle: boolean,
  isShowDefaultInCompare: boolean = false,
  isDarkMode: boolean = true,
  scoutReports: (PlayerDetailScoutReportsFragment | null)[] = [],
  isFilterNullMetrics: boolean = true
) => {
  const baseMetrics = getPlayerBaseMetrics(
    bioData,
    currencyCode,
    isDefaultStyle,
    isDarkMode
  );
  const categoryCalMetrics = getPlayerCategoricalMetrics(
    bioData,
    isShowDefaultInCompare,
    isDarkMode
  );

  // Always show reports metric
  const reportsMetric = [getPlayerReportsMetric(scoutReports, isDarkMode)];

  // Get dynamic custom metrics
  const customMetrics = getPlayerCustomCompositeMetrics(bioData, isDarkMode);

  const hasValidValue = (inputItem: ICompositeMetric) => {
    if (!isFilterNullMetrics) return true;
    if (!inputItem.formattedValue || inputItem.formattedValue === "N/A")
      return false;

    // Hide "Months Remaining" metric if value is 0 or less
    if (inputItem.label === "Months Remaining") {
      const valueStr = inputItem.formattedValue || "0";
      // Only accept valid integer strings (no suffixes, no decimals)
      if (!/^-?\d+$/.test(valueStr)) return false;
      const numericValue = Number(valueStr);
      if (numericValue <= 0) return false;
    }

    return true;
  };

  return [
    ...baseMetrics.filter(hasValidValue),
    ...categoryCalMetrics.filter(hasValidValue),
    ...customMetrics.filter(hasValidValue),
    ...reportsMetric.filter(hasValidValue),
  ] as ICompositeMetric[];
};
